import { energyConditionDefinition } from '../../../nodes/definitions/conditions/energy-condition';
import { scheduleConditionDefinition } from '../../../nodes/definitions/conditions/schedule-condition';
import { sunriseSunsetConditionDefinition } from '../../../nodes/definitions/conditions/sunrise-sunset-condition';
import { timeConditionDefinition } from '../../../nodes/definitions/conditions/time-condition';
import { weatherConditionDefinition } from '../../../nodes/definitions/conditions/weather-condition';
import type { NodeDefinition } from '../../../nodes/definitions/types';
import type { FlowCardDefinition } from '../types';

const VERSION = '1.0.0';

function compileWithMetadata(definition: NodeDefinition, cardId: string): FlowCardDefinition['compile'] {
	return (node, context) => {
		const compiled = definition.toSceneJson(node, context);
		return compiled && typeof compiled === 'object'
			? {
					...compiled,
					_fid: (compiled as any)._fid || node.id,
					_flow_card: cardId,
					_flow_card_version: VERSION,
				}
			: compiled;
	};
}

const weekdayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function timeSummary(data: Record<string, any>): string {
	const type = String(data.timeSubType || 'daily');
	if (type === 'timer') return `Every ${Number(data.interval ?? 30)} minutes`;
	if (type === 'ot_cond' || type === 'execute_once')
		return `Once on ${data.date || 'a date'} at ${data.executeTime || data.time || 'a time'}`;
	const days = Array.isArray(data.weekdays) ? data.weekdays : [1, 2, 3, 4, 5];
	const dayLabel = days.length === 7 ? 'every day' : days.map((day) => weekdayNames[day]).join(', ');
	return `At ${data.time || '08:00'} on ${dayLabel}`;
}

function timeCard(executeOnce: boolean): FlowCardDefinition {
	return {
		type: 'timeCondition',
		...(executeOnce ? { paletteKey: 'executeOnce' } : {}),
		version: VERSION,
		domain: 'core',
		role: 'condition',
		category: 'conditions',
		label: executeOnce ? 'Execute Once' : 'Time of Day',
		description: executeOnce
			? 'Run once at a specific date and time'
			: 'Run on selected days, at a time, or at a repeating interval',
		icon: executeOnce ? 'fa-calendar-check' : 'fa-clock',
		color: '#06b6d4',
		schema: {
			$schema: 'https://json-schema.org/draft/2020-12/schema',
			type: 'object',
			properties: {
				timeSubType: {
					type: 'string',
					enum: ['timer', 'daily', 'd_cond', 'ot_cond', 'execute_once'],
				},
				weekdays: { type: 'array' },
				time: { type: 'string' },
				interval: { type: 'number', minimum: 1 },
				date: { type: 'string' },
				executeTime: { type: 'string' },
			},
		},
		fields: [{ path: 'timeSubType', label: 'Time settings', type: 'time-configuration' }],
		ports: { sources: ['true'] },
		defaults: () => ({
			timeSubType: executeOnce ? 'ot_cond' : 'daily',
			weekdays: [1, 2, 3, 4, 5],
			time: '08:00',
			interval: 30,
			date: new Date().toISOString().split('T')[0],
			executeTime: '12:00',
			cardVersion: VERSION,
		}),
		summary: timeSummary,
		validate: (data) => {
			if (data.sceneData || data.originalCondition) return [];
			const type = executeOnce ? 'ot_cond' : String(data.timeSubType || 'daily');
			if (type === 'timer')
				return Number(data.interval) > 0
					? []
					: [{ path: 'interval', message: 'Set an interval greater than zero' }];
			if (type === 'ot_cond' || type === 'execute_once') {
				const issues = [];
				if (!String(data.date || '').trim())
					issues.push({ path: 'date', message: 'Select a date' });
				if (!String(data.executeTime || data.time || '').trim())
					issues.push({ path: 'executeTime', message: 'Select a time' });
				return issues;
			}
			return String(data.time || '').trim() ? [] : [{ path: 'time', message: 'Select a time' }];
		},
		compile: compileWithMetadata(
			timeConditionDefinition,
			executeOnce ? 'executeOnce' : 'timeCondition',
		),
		migrate: (data) => ({
			...data,
			timeSubType: executeOnce ? 'ot_cond' : data.timeSubType || 'daily',
			weekdays: Array.isArray(data.weekdays) ? data.weekdays : [1, 2, 3, 4, 5],
			interval: Number(data.interval ?? data.dminslimit ?? 30),
			executeTime: data.executeTime ?? data.time ?? '12:00',
		}),
	};
}

export const timeConditionCards = [timeCard(false), timeCard(true)];

export const scheduleConditionCard: FlowCardDefinition = {
	type: 'scheduleCondition',
	version: VERSION,
	domain: 'core',
	role: 'condition',
	category: 'conditions',
	label: 'Schedule',
	description: 'Allow the flow to continue during one or more weekly time windows',
	icon: 'fa-calendar',
	color: '#6366f1',
	schema: {
		$schema: 'https://json-schema.org/draft/2020-12/schema',
		type: 'object',
		required: ['activeTimeIntervals'],
		properties: { activeTimeIntervals: { type: 'array' } },
	},
	fields: [{ path: 'activeTimeIntervals', label: 'Schedule windows', type: 'schedule-configuration' }],
	ports: { sources: ['true', 'false'] },
	defaults: () => ({ activeTimeIntervals: [], cardVersion: VERSION }),
	summary: (data) => {
		const count = Array.isArray(data.activeTimeIntervals) ? data.activeTimeIntervals.length : 0;
		return count
			? `${count} active schedule ${count === 1 ? 'window' : 'windows'}`
			: 'Add a schedule window';
	},
	validate: (data) =>
		data.sceneData || (Array.isArray(data.activeTimeIntervals) && data.activeTimeIntervals.length)
			? []
			: [{ path: 'activeTimeIntervals', message: 'Add at least one active interval' }],
	compile: compileWithMetadata(scheduleConditionDefinition, 'scheduleCondition'),
	migrate: (data) => ({
		...data,
		activeTimeIntervals: Array.isArray(data.activeTimeIntervals)
			? data.activeTimeIntervals
			: Array.isArray((data.sceneData as any)?.indailyint)
				? (data.sceneData as any).indailyint
				: [],
	}),
};

function solarSummary(data: Record<string, any>): string {
	const event = String(data.sunType || data.type || 'sunrise') === 'sunset' ? 'sunset' : 'sunrise';
	const minutes = Number(data.hours ?? 0) * 60 + Number(data.minutes ?? data.mins ?? 0);
	return minutes
		? `${minutes} minutes ${data.beforeAfter === 'before' ? 'before' : 'after'} ${event}`
		: `At ${event}`;
}

export const sunriseSunsetConditionCard: FlowCardDefinition = {
	type: 'sunriseSunsetCondition',
	version: VERSION,
	domain: 'core',
	role: 'condition',
	category: 'conditions',
	label: 'Sunrise / Sunset',
	description: 'Run at sunrise or sunset, with an optional offset for the home location',
	icon: 'fa-sun',
	color: '#f59e0b',
	premiumOnly: true,
	schema: {
		$schema: 'https://json-schema.org/draft/2020-12/schema',
		type: 'object',
		properties: {
			sunType: { type: 'string', enum: ['sunrise', 'sunset'] },
			beforeAfter: { type: 'string', enum: ['before', 'after'] },
			hours: { type: 'number', minimum: 0 },
			minutes: { type: 'number', minimum: 0 },
			locid: { type: 'string' },
		},
	},
	fields: [{ path: 'sunType', label: 'Sun event', type: 'solar-configuration' }],
	ports: { sources: ['true', 'false'] },
	defaults: () => ({
		sunType: 'sunrise',
		beforeAfter: 'after',
		hours: 0,
		minutes: 0,
		locid: '',
		cardVersion: VERSION,
	}),
	summary: solarSummary,
	compile: compileWithMetadata(sunriseSunsetConditionDefinition, 'sunriseSunsetCondition'),
	migrate: (data) => ({
		...data,
		sunType: data.sunType ?? data.type ?? 'sunrise',
		beforeAfter: data.beforeAfter ?? data.before_after ?? 'after',
		hours: Number(data.hours ?? 0),
		minutes: Number(data.minutes ?? data.mins ?? 0),
	}),
};

function weatherSummary(data: Record<string, any>): string {
	const condition = String(data.weatherCond || data.cond || 'weather').replaceAll('_', ' ');
	const operator = String(data.operator || data.op || 'equal').replaceAll('_', ' ');
	const offset = Number(data.hourOffset ?? data.hours ?? 0);
	return `${condition}: ${operator} ${data.value ?? ''}${offset ? `, ${offset}h ahead` : ''}`.trim();
}

export const weatherConditionCard: FlowCardDefinition = {
	type: 'weatherCondition',
	version: VERSION,
	domain: 'core',
	role: 'condition',
	category: 'conditions',
	label: 'Weather',
	description: 'Compare current or forecast weather for the configured home location',
	icon: 'fa-cloud-sun',
	color: '#0ea5e9',
	premiumOnly: true,
	schema: {
		$schema: 'https://json-schema.org/draft/2020-12/schema',
		type: 'object',
		properties: {
			weatherCond: { type: 'string' },
			operator: { type: 'string' },
			executionType: { type: 'string', enum: ['once', 'any'] },
			hourOffset: { type: 'number', minimum: 0 },
			locid: { type: 'string' },
		},
	},
	fields: [{ path: 'weatherCond', label: 'Weather condition', type: 'weather-configuration' }],
	ports: { sources: ['true', 'false'] },
	defaults: () => ({
		weatherCond: 'temperature',
		operator: 'equal',
		value: '',
		executionType: 'once',
		hourOffset: 0,
		locid: '',
		cardVersion: VERSION,
	}),
	summary: weatherSummary,
	validate: (data) =>
		data.sceneData || (data.value !== '' && data.value != null)
			? []
			: [{ path: 'value', message: 'Set a weather value' }],
	compile: compileWithMetadata(weatherConditionDefinition, 'weatherCondition'),
	migrate: (data) => ({
		...data,
		weatherCond: data.weatherCond ?? data.cond ?? 'temperature',
		operator: data.operator ?? data.op ?? 'equal',
		executionType: data.executionType ?? data.execution_type ?? 'once',
		hourOffset: Number(data.hourOffset ?? data.hours ?? 0),
	}),
};

export const energyConditionCard: FlowCardDefinition = {
	type: 'energyCondition',
	version: VERSION,
	domain: 'energy',
	role: 'condition',
	category: 'conditions',
	label: 'Energy',
	description: 'Compare the electricity exchange price for a selected market',
	icon: 'fa-bolt-lightning',
	color: '#facc15',
	schema: {
		$schema: 'https://json-schema.org/draft/2020-12/schema',
		type: 'object',
		properties: {
			exchangeMarket: { type: 'string' },
			operator: { type: 'string', enum: ['less than', 'more than'] },
			value: { type: 'number' },
			minutes: { type: 'number', minimum: 0 },
		},
	},
	fields: [{ path: 'exchangeMarket', label: 'Energy price', type: 'energy-configuration' }],
	ports: { sources: ['true', 'false'] },
	defaults: () => ({
		exchangeMarket: 'Austria',
		operator: 'less than',
		value: 0,
		minutes: 0,
		cardVersion: VERSION,
	}),
	summary: (data) =>
		`Energy price ${data.operator === 'more than' ? 'above' : 'below'} ${Number(data.value ?? 0)} EUR/MWh in ${String(data.exchangeMarket || 'the selected market').replaceAll('|', ' – ')}`,
	compile: compileWithMetadata(energyConditionDefinition, 'energyCondition'),
	migrate: (data) => ({
		...data,
		exchangeMarket: data.exchangeMarket ?? data.exchange_market ?? 'Austria',
		operator: data.operator ?? 'less than',
		value: Number(data.value ?? 0),
		minutes: Number(data.minutes ?? 0),
	}),
};

export const providerConditionCards = [
	...timeConditionCards,
	scheduleConditionCard,
	sunriseSunsetConditionCard,
	weatherConditionCard,
	energyConditionCard,
];
